import { useEffect, useState } from "react";
import { GlobalTable } from "../../../global/components/GlobalTable";
import { useNavigate, useSearchParams } from "react-router-dom";
import useGet from "../../../global/hooks/useGet";
import ConfirmModal from "../../../global/components/ConfirmModal";
import useDelete from "../../../global/hooks/useDelete";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import { LimitedCardsFilter } from "../Components/FilterComponent";
import { LimitedCardsColumns } from "./Columns";

const initialFilterState = {
  LimitedCardName: "",
  LicenseItem: null,
  LicenseCode: "",
  Term: null,
};

export const LimitedCards = () => {
  const [hoveredRow, setHoveredRow] = useState(null);
  const [listData, setListData] = useState([]);
  const [params, setParams] = useSearchParams({
    limit: 10,
    sort_by: "LimitedCardNo",
    order: "asc",
  });
  const modParams = { ...Object.fromEntries([...params?.entries()]) } || {};
  const initialPagination = {
    current: parseInt(modParams.page) || 1,
    pageSize: parseInt(modParams.limit) || 10,
    total: 0,
  };
  const [pagination, setPagination] = useState(initialPagination);
  const [filter, setFilter] = useState(initialFilterState);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [showModal, setShowModal] = useState(false);

  const navigate = useNavigate();

  const breadcrumbItems = [
    {
      label: "Limited License",
      href: "/limited-license",
    },
  ];

  const query = new URLSearchParams(params);
  query.delete("pageSize");
  query.delete("total");
  query.delete("current");
  const updatedQuery = query.toString();
  const url = updatedQuery
    ? `${process.env.REACT_APP_API_URL}/api/limited/cards/?${updatedQuery}`
    : `${process.env.REACT_APP_API_URL}/api/limited/cards/`;
  const { data, loading, error, refetch } = useGet(url);

  let deleteUrl;
  selectedRowKeys.length > 1
    ? (deleteUrl = `${process.env.REACT_APP_API_URL}/api/limited/cards/bulk-delete/`)
    : (deleteUrl = `${process.env.REACT_APP_API_URL}/api/limited/cards/${selectedRowKeys}/`);

  const {
    data: deletedData,
    error: deletedDataError,
    deleteData,
    deleteMultiple,
  } = useDelete(deleteUrl);

  useEffect(() => {
    const matchedFilter = {};
    for (const key in initialFilterState) {
      if (modParams[key] !== undefined) {
        matchedFilter[key] = modParams[key];
      }
    }

    setFilter((prev) => ({
      ...prev,
      ...matchedFilter,
    }));
  }, []);

  const paginationConfig = {
    current: pagination.current,
    pageSize: pagination.pageSize,
    total: data?.count,
    showSizeChanger: true,
    pageSizeOptions: [10, 20, 50, 100],
    showTotal: (total, range) => `Total ${total} items ${range[0]}-${range[1]}`,
    onChange: (page, pageSize) => {
      setPagination({
        ...pagination,
        current: page,
        pageSize: pageSize,
      });

      const updatedParams = {
        ...Object.fromEntries([...params.entries()]),
        limit: pageSize,
        page: page,
      };
      setParams(updatedParams);
    },
  };

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    fixed: true,
  };

  const onRow = (record) => {
    return {
      onClick: (event) => {
        navigate(`/limited-license/info/${record?.LimitedCardNo}`);
      },
    };
  };

  const handleFilterChange = (e) => {
    const inputName = e.target.name;
    const inputValue = e.target.value.replace(/['""`''""']/g, "");
    setFilter({ ...filter, [inputName]: inputValue });
  };

  const handleFilter = (e) => {
    const filterDict = {};
    for (const key in filter) {
      if (
        filter[key] !== null &&
        filter[key] !== "" &&
        filter[key] !== undefined
      ) {
        filterDict[key] = filter[key];
      }
    }
    const isEmpty = JSON.stringify(filterDict) === "{}";

    if (isEmpty) {
      handleReset();
    } else {
      const params = {
        limit: 10,
        sort_by: "LimitedCardNo",
        order: "asc",
        page: 1,
        ...filterDict,
      };
      setPagination((prev) => ({ ...prev, current: 1 }));
      setParams(params);
      refetch();
    }
  };

  const handleReset = (e) => {
    setFilter(initialFilterState);
    setPagination({
      current: 1,
      pageSize: 10,
      total: 0,
    });
    const params = {
      limit: 10,
      sort_by: "LimitedCardNo",
      order: "asc",
    };

    setParams(params);
    refetch();
  };

  const onDeleteConfirm = async () => {
    try {
      if (selectedRowKeys.length === 1) {
        await deleteData();
      } else {
        await deleteMultiple({ ids: selectedRowKeys });
      }
    } catch (error) {
      openErrorNotification("Something went wrong.");
    }
    setShowModal(false);
    setSelectedRowKeys([]);
  };

  useEffect(() => {
    if (deletedData && !deletedDataError) {
      openConfirmNotification("Success");
      refetch();
    } else if (deletedDataError) {
      openErrorNotification(
        deletedDataError?.response?.data?.message || "Something went wrong",
      );
    }
  }, [deletedData, deletedDataError]);

  useEffect(() => {
    if (data && !loading) {
      const newData = data?.data?.map((el) => ({
        ...el,
        key: el?.LimitedCardNo,
      }));
      setListData(newData);
    }
  }, [data, loading]);

  return (
    <div>
      <div className="pb-4 -mt-4 pt-[6px]">
        <CommonBreadcrumb breadcrumbItems={breadcrumbItems} />
      </div>
      <div className="p-4 bg-white rounded-[4px] border border-gray-200">
        <LimitedCardsFilter
          filter={filter}
          handleFilterChange={handleFilterChange}
          handleFilter={handleFilter}
          handleReset={handleReset}
          setShowModal={setShowModal}
          selectedRowKeys={selectedRowKeys}
          setFilter={setFilter}
        />
        <ConfirmModal
          showModal={showModal}
          setShowModal={setShowModal}
          onDeleteConfirm={onDeleteConfirm}
          setSelectedRowKeys={setSelectedRowKeys}
        />
        <div className="mt-4">
          <GlobalTable
            columns={LimitedCardsColumns(
              hoveredRow,
              setShowModal,
              setSelectedRowKeys,
              setParams,
              params,
            )}
            dataSource={listData}
            rowSelection={rowSelection}
            pagination={paginationConfig}
            onRow={(record, rowIndex) => {
              return {
                onMouseEnter: () => setHoveredRow(rowIndex),
                onMouseLeave: () => setHoveredRow(null),
                ...onRow?.(record, rowIndex),
              };
            }}
            loading={loading}
          />
        </div>
      </div>
    </div>
  );
};
